import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { APIURL } from "../../../config";
import moment from "moment";
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from "../../DataGrid/ControlledDataGrid";
import { TextField, Button, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Tooltip, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

export default function AccountsReceivable(props) {
    const [gridId, setGridId] = useState(58);
    const apiRef = useGridApiRef();

    let currDataGridRows = useRef([]);
    const [dataGridRows, setDataGridRows] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(moment().startOf("month"));
    const [isLoading, setIsLoading] = useState(true);
    const [editNote, setEditNote] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [totRec, setTotRec] = useState(0);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');

    useEffect(() => {
        fetchData();
    }, [currentMonth, alertMessage]);

    const fetchData = async () => {
        await axios
            .get(APIURL + "/accountsReceivable", {
                params: { companyID: props.comp_id, month: moment(currentMonth).format('YYYYMM') },
            }).then((response) => {
                getDataGridRows(response.data);
                setTotRec(response.data.reduce((sum, rec) => sum + rec.total, 0));
            }).catch((error) => {
                console.log(error);
            });

        setIsLoading(false);
    };

    const columns = [
        { field: "billable_id", headerName: "Billable ID" },
        { field: "DDDID", headerName: "DDDID", width: 75 },
        { field: "customer_id", headerName: "Customer ID" },
        { field: "customer_name", headerName: "Customer Name", width: 200 },
        { field: "customer_status", headerName: "Customer Status" },
        { field: "customer_TransferOutDate", headerName: "Customer Transfer Out Date" },
        { field: "customer_TransferOutReason", headerName: "Customer Transfer Out Reason" },
        { field: "dob", headerName: "Customer DOB", width: 120 },
        { field: "plan_approval_date", headerName: "Active Plan Creation Date", width: 120 },
        { field: "AgencyAssignDate", headerName: "Agency Assign Date", width: 120 },
        { field: "project_id", headerName: "Project ID" },
        { field: "project_name", headerName: "Project Name", width: 200 },
        { field: "plan_version", headerName: "Plan Version", width: 80 },
        { field: "plan_program", headerName: "Plan Program", width: 80 },
        { field: "pro_starts_on", headerName: "Project Start Date", width: 100 },
        { field: "pro_ends_on", headerName: "Project End Date", width: 100 },
        { field: "proc_code", headerName: "Proc Code", width: 100 },
        { field: "unit", headerName: "Unit", width: 60 },
        { field: "rate", headerName: "Rate", width: 50 },
        { field: "total", headerName: "Total", width: 70 },
        { field: "account_code", headerName: "Account Code", width: 175 },
        { field: "billable_starts_on", headerName: "Billable Start Date", width: 100 },
        { field: "billable_ends_on", headerName: "Billable End Date", width: 100 },
        { field: "post_date", headerName: "Post Date", width: 100 },
        {
            field: "priorAuthStatus",
            headerName: "Prior Auth Status",
            width: 150,
            editable: true,
            type: 'singleSelect',
            valueOptions: ['On DDD Voucher'],
            renderCell: (params) => (
                <Tooltip title="Double-click to change status">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: 'pointer' }}>
                        <span>{params.value}</span>
                        <ArrowDropDownIcon fontSize="small" sx={{ color: 'action.active', flexShrink: 0 }} />
                    </Box>
                </Tooltip>
            ),
        },
        {
            field: "paymentStatus",
            headerName: "Payment Status",
            width: 200,
            editable: true,
            type: 'singleSelect',
            valueOptions: (params) => {
                const options = ['Voucher Payment Received'];
                if (params.row?.claim_id) {
                    options.push('Payment Received');
                }
                return options;
            },
            renderCell: (params) => (
                <Tooltip title="Double-click to change status">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: 'pointer' }}>
                        <span>{params.value}</span>
                        <ArrowDropDownIcon fontSize="small" sx={{ color: 'action.active', flexShrink: 0 }} />
                    </Box>
                </Tooltip>
            ),
        },
        { field: "paymentDate", headerName: "Payment Date", width: 90 },
        {
            field: "notes",
            headerName: "Notes",
            width: 300,
            editable: true,
            renderCell: (params) => (
                <Tooltip title="Double-click to edit note">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', cursor: 'pointer' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params.value}</span>
                        <EditNoteIcon fontSize="small" sx={{ color: 'action.active', flexShrink: 0, ml: 0.5 }} />
                    </Box>
                </Tooltip>
            ),
        },
    ];

    function getDataGridRows(billableData) {
        let rows = billableData.map((data, index) => {
            let billableStartDate = '', billableEndDate = '';

            if (data.rate === 0) {
                billableStartDate = '-';
                billableEndDate = '-';
            } else if (data.account_code === 'ACTIVE PLAN' || data.account_code === 'PLAN RENEWED') {
                billableStartDate = moment(data.YYYYMM).startOf('month');
                billableEndDate = moment(data.YYYYMM).utc().endOf('month');
            } else if (data.account_code === 'PLAN CHANGE') {
                const postDate = moment(data.post_date).utc().format('MM/DD/YYYY');

                if (Math.abs(moment(postDate).diff(moment(data.YYYYMM).startOf('month'), 'days')) === (data.unit - 1)) {
                    billableStartDate = moment(data.YYYYMM).startOf('month');
                    billableEndDate = data.post_date;
                } else {
                    billableStartDate = data.post_date;
                    billableEndDate = moment(data.YYYYMM).utc().endOf('month');
                }
            } else if (data.account_code === 'INITIAL PLAN') {
                if (data.proc_code === 'T2024HI') {
                    billableStartDate = moment(data.YYYYMM).startOf('month');
                    billableEndDate = moment(data.YYYYMM).utc().endOf('month');
                } else if (data.proc_code === 'T2024HI:52') {
                    billableStartDate = moment(data.active_plan_creation_date);
                    billableEndDate = moment(data.YYYYMM).utc().endOf('month');
                } else {
                    billableStartDate = '-';
                    billableEndDate = '-';
                }
            } else if (data.account_code === 'PLAN TERMINATION') {
                if (data.cus_status === 'Dormant') {
                    billableStartDate = '';
                    billableEndDate = '';
                } else {
                    billableStartDate = moment(data.YYYYMM).startOf('month');
                    billableEndDate = data.pro_ends_on;
                }
            } else {
                billableStartDate = '';
                billableEndDate = '';
            }

            return {
                id: index,
                billable_id: data.billable_id,
                DDDID: Array.isArray(data.DDDID) ? data.DDDID[0] : data.DDDID,
                customer_id: data.customer_id,
                customer_name: data.cus_name,
                customer_status: data.cus_status,
                customer_TransferOutDate: data.TransferOutDate ? moment(data.TransferOutDate).utc().format('MM/DD/YYYY') : '-',
                customer_TransferOutReason: data.TransferOutReason,
                dob: data.dob ? moment(data.dob).utc().format('MM/DD/YYYY') : '-',
                plan_approval_date: data.active_plan_creation_date ? moment(data.active_plan_creation_date).utc().format('MM/DD/YYYY') : '-',
                AgencyAssignDate: data.AgencyAssignDate ? moment(data.AgencyAssignDate).utc().format('MM/DD/YYYY') : '-',
                project_id: data.project_id,
                project_name: data.pro_title,
                plan_version: data.plan_version !== 'null' ? data.plan_version : '',
                plan_program: data.plan_program,
                pro_starts_on: data.pro_starts_on ? moment(data.pro_starts_on).utc().format('MM/DD/YYYY') : '-',
                pro_ends_on: data.pro_ends_on ? moment(data.pro_ends_on).utc().format('MM/DD/YYYY') : '-',
                proc_code: data.proc_code,
                unit: data.unit,
                rate: data.rate,
                total: data.total,
                account_code: data.account_code,
                billable_starts_on: (billableStartDate && billableStartDate !== '-') ? moment(billableStartDate).utc().format('MM/DD/YYYY') : '-',
                billable_ends_on: (billableEndDate && billableEndDate !== '-') ? moment(billableEndDate).utc().format('MM/DD/YYYY') : '-',
                post_date: data.post_date ? moment(data.post_date).utc().format('MM/DD/YYYY') : '-',
                priorAuthStatus: data.prior_auth_status ? data.prior_auth_status : data.PA_FOUND ? 'PA Found' : 'No PA Found',
                paymentStatus: data.payment_status ? data.payment_status : data.PAYMENT_FOUND ? data.PAYMENT_FOUND[0]?.Status : 'No Payment Found',
                paymentDate: data.PAYMENT_FOUND ? moment(data.PAYMENT_FOUND[0]?.CreateDate).utc().format('MM/DD/YYYY') : '-',
                claim_id: data.PAYMENT_FOUND ? data.PAYMENT_FOUND[0]?.claim_ID : null,
                billing_note_id: data.billing_note_id,
                notes: data.billing_note,
            };
        });
        currDataGridRows.current = rows;
        setDataGridRows(currDataGridRows.current);
    }

    const handleCellDoubleClick = (params) => {
        if (params.field === "notes") {
            setEditNote(true);
            setEditRow(params.row);
            setNoteText(params.row.notes);
        }
    };

    const handleCellEditCommit = async (updatedRow, originalRow) => {
        try {
            if (originalRow.priorAuthStatus !== updatedRow.priorAuthStatus) {
                await updatePriorAuthData(updatedRow.billable_id);
            }

            if (originalRow.paymentStatus !== updatedRow.paymentStatus) {
                await updateClaimData(updatedRow.billable_id, updatedRow.paymentStatus, updatedRow.claim_id);
            }
        } catch (error) {
            setAlertMessage(error || 'Update failed.');
            setAlertType('error');
        }

        return updatedRow;
    };

    const updatePriorAuthData = async (id) => {
        await axios
            .put(APIURL + "/accountsReceivable/updatePriorAuth", {
                billable_id: id,
            }).then(function (response) {
                setAlertMessage('Prior Auth Status updated successfully!');
                setAlertType('success');
            })
            .catch(function (error) {
                console.log(error);
                setAlertMessage('An error occurred while updating Prior Auth Status!');
                setAlertType('error');
            });
    };

    const updateClaimData = async (id, paymentStatus, claimId) => {
        await axios
            .put(APIURL + "/accountsReceivable/updateClaim", {
                billable_id: id,
                payment_status: paymentStatus,
                ...(claimId && { claim_ID: claimId }),
            })
            .then(function (response) {
                setAlertMessage('Payment Status updated successfully!');
                setAlertType('success');
            })
            .catch(function (error) {
                console.log(error);
                setAlertMessage('An error occurred while updating Payment Status!');
                setAlertType('error');
            });;
    };

    const updateBillingNote = async (id, note) => {
        try {
            if (note && note.replace(/\s/g, '').length !== 0) {
                await axios.put(APIURL + "/accountsReceivable/billingNote", {
                    billing_note_id: id,
                    billing_note: note,
                    empID: props.user.emp_id,
                });
                setAlertMessage('Note successfully saved.');
                setAlertType('success');
            } else {
                setAlertMessage('Note cannot be empty!');
                setAlertType('error');
            }
        } catch (error) {
            setAlertMessage(error.message);
            setAlertType('error');
        }
        setEditNote(false);
        setEditRow(null);
    };

    const addBillingNote = async (id, note) => {
        try {
            if (note && note.replace(/\s/g, '').length !== 0) {
                await axios.post(APIURL + "/accountsReceivable/billingNote", {
                    billable_id: id,
                    billing_note: note,
                    empID: props.user.emp_id,
                });
                setAlertMessage('Note successfully saved.');
                setAlertType('success');
            } else {
                setAlertMessage('Note cannot be empty!');
                setAlertType('error');
            }
        } catch (error) {
            setAlertMessage(error.message);
            setAlertType('error');
        }
        setEditNote(false);
        setEditRow(null);
    };

    const getNoteDialog = () => {
        return (
            <Dialog
                open={editNote}
                onClose={() => { setEditNote(false); setEditRow(null); }}
                maxWidth="md"
                fullWidth
                disableEscapeKeyDown
            >
                <DialogTitle>{editRow.billing_note_id ? 'Edit Note' : 'Add Note'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            multiline
                            value={noteText}
                            onChange={(event) => {
                                setNoteText(event.target.value);
                            }}
                            style={{ margin: '5px', width: '100%' }}
                            inputProps={{ maxLength: 2000 }}
                            minRows={8}
                            label="Note"
                            variant="outlined"
                            id="bNote"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        fullWidth
                        sx={{
                            padding: '10px',
                            margin: '5px',
                        }}
                        onClick={() => editRow.billing_note_id ? updateBillingNote(editRow.billing_note_id, noteText) : addBillingNote(editRow.billable_id, noteText)}
                    >
                        {editRow.billing_note_id ? 'Edit Note' : 'Add Note'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const SnackbarComponent = () => {
        return (
            <div>
                <Snackbar open={alertMessage ? true : false} autoHideDuration={2000} onClose={handleSnackbarAlertClose}>
                    <Alert severity={alertType} elevation={6} variant="filled">
                        {alertMessage}
                        <IconButton
                            sx={{ p: 0.25 }}
                            onClick={handleSnackbarAlertClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Alert>
                </Snackbar>
            </div>
        );
    };

    const handleSnackbarAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertMessage('');
        setAlertType('');
    };

    const onDateChange = (newValue) => {
        setCurrentMonth(moment(newValue).startOf("month"));
    };

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                        size="large"
                        variant="contained"
                        onClick={() => onDateChange(moment(currentMonth).subtract(1, "month"))}
                    >
                        <i className="fas fa-arrow-left"></i>
                    </Button>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Select Month"
                            views={["year", "month"]}
                            sx={{ marginLeft: 1, marginRight: 1 }}
                            slotProps={{ textField: { size: 'small' } }}
                            value={new Date(currentMonth)}
                            onChange={(newValue) => {
                                onDateChange(newValue);
                            }}
                        />
                    </LocalizationProvider>
                    <Button
                        size="large"
                        variant="contained"
                        onClick={() => onDateChange(moment(currentMonth).add(1, "month"))}
                    >
                        <i className="fas fa-arrow-right"></i>
                    </Button>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignSelf: 'center',
                        background: '#1976D2',
                        borderRadius: 999,
                        padding: 8,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        minWidth: 200,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                        Total Receivable: ${totRec?.toFixed(2)}
                    </Typography>
                </div>
            </div>
            <div style={{ height: "calc(100vh - 220px)" }}>
                <ControlledDataGrid
                    gridId={gridId}
                    user={props.user}
                    apiRef={apiRef}
                    initialState={{ sorting: { sortModel: [{ field: "post_date", sort: "desc" }] } }}
                    getRowId={(row) => row.id}
                    rows={dataGridRows}
                    columns={columns}
                    checkboxSelection={false}
                    disableRowSelectionOnClick
                    onCellDoubleClick={handleCellDoubleClick}
                    processRowUpdate={(updatedRow, originalRow) => handleCellEditCommit(updatedRow, originalRow)}
                    onProcessRowUpdateError={(error) =>
                        console.error("Error updating row:", error)
                    }
                    loading={isLoading}
                    sx={{
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        },
                    }}
                />
            </div>
            {editNote && getNoteDialog()}
            {alertMessage && SnackbarComponent()}
        </div>
    );
}